import { Fluent, GroupInfo, faIcon, htmlEncode, indexOf } from "@serenity-is/corelib";
import { Column, EventEmitter, Grid, Group, NonDataRow } from "@serenity-is/sleekgrid";
import { DraggableGroupingMixinTexts } from "../ServerTypes/Texts";

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        grouping?: false | GroupInfo<any>;
    }
}

export interface DraggableGroupingOptions {
    deleteIconCssClass?: string;
    dropPlaceHolderText?: string;
    getGroupingFor?: (column: Column) => GroupInfo<any>;
    getAllColumns?: () => Column[];
    getCount?: (group: Group<any>) => number;
    displayTotalsRow?: boolean;
    collapsed?: boolean;
}

export interface GroupChangeInfo {
    groupingArray: any[];
    userChange: boolean;
}

/**
 * Based on plugin at https://github.com/muthukumarse/Slickgrid
 */
export class DraggableGroupingPlugin {

    declare private grid: Grid;
    declare private gridUid: string;
    declare private dropbox: Fluent;
    declare private dropboxPlaceholder: HTMLElement;
    declare private expandAll: Fluent;
    declare private collapseAll: Fluent;
    declare private options: DraggableGroupingOptions;
    declare private columnsGroupBy: Column[];
    public readonly onGroupChanged = new EventEmitter<GroupChangeInfo>();

    constructor(options?: DraggableGroupingOptions) {
        var defaults: DraggableGroupingOptions = {
            deleteIconCssClass: faIcon("cross")
        };

        this.options = Object.assign({}, defaults, options);
    }

    public init(grid: Grid) {
        this.grid = grid;
        this.gridUid = grid['getUID']();

        this.dropbox = Fluent(grid.getGroupingPanel());
        var dropPlaceHolderText = this.options.dropPlaceHolderText ||
            DraggableGroupingMixinTexts.DropPlaceholder;

        this.dropbox.append(<>
            <div class="slick-dropped-placeholder" ref={el => this.dropboxPlaceholder = el}>{dropPlaceHolderText}</div>
            <div class="slick-group-expand-all" title={DraggableGroupingMixinTexts.ExpandAllButton} style="display:none"></div>
            <div class="slick-group-collapse-all" title={DraggableGroupingMixinTexts.CollapseAllButton} style="display:none"></div>
        </>);

        this.expandAll = this.dropbox.findFirst(".slick-group-expand-all");
        this.collapseAll = this.dropbox.findFirst(".slick-group-collapse-all");
        this.setupColumnDropbox();

        var cols = this.getColumns();
        for (var i = 0; i < cols.length; i++) {
            var col = cols[i];
            grid['updateColumnHeader'](col.id);
        }

        this.columnsGroupBy = [];
    }

    private getColumns(): Column[] {
        return (this.options.getAllColumns && this.options.getAllColumns()) ||
            this.grid.getColumns();
    }

    public destroy() {
        this.onGroupChanged.clear();
    }

    private setupColumnDropbox() {
        let dropbox = this.dropbox.getNode();
        dropbox.addEventListener("dragover", e => e.preventDefault());

        const isValidDrop = (target: HTMLElement) => {
            var id = target?.dataset?.id;
            let validDrop = !!(id && target?.classList?.contains?.("slick-header-column"));
            if (validDrop) {
                this.getColumns().forEach((e) => {
                    if (e.id == id && e.grouping === false) {
                        validDrop = false;
                    }
                });
            }
            return validDrop;
        }

        const setValidClass = (valid: boolean) => {
            dropbox.classList.toggle('drop-valid', valid != null && !!valid);
            dropbox.classList.toggle('drop-invalid', valid != null && !valid);
        }

        let draggedId: string;
        let dragEnterCount = 0;
        dropbox.addEventListener("dragenter", e => {
            if (++dragEnterCount > 1)
                return;

            // @ts-ignore
            let from: HTMLElement = typeof Sortable !== undefined ? Sortable.dragged :
                (e.relatedTarget ?? (e as any).fromElement);
            from = from?.closest?.('.slick-header-column') ?? from;

            if (!from) {
                dropbox.classList.add('drop-invalid');
                draggedId = null;
                return;
            }

            draggedId = from?.dataset?.id;
            setValidClass(isValidDrop(from));
        });

        dropbox.addEventListener("dragleave", e => {
            if (--dragEnterCount <= 0) {
                dragEnterCount = 0;
                setValidClass(null);
                return;
            }
        });

        dropbox.addEventListener("drop", (e) => {
            dragEnterCount = 0;
            e.preventDefault();
            setValidClass(null);
            if (!draggedId)
                return;
            this.addGroupColumn(draggedId);
            this.groupChanged(true);
        });

        // @ts-ignore
        if (typeof Sortable !== "undefined") {
            // @ts-ignore
            let sortableInstance = Sortable.create(dropbox, {
                animation: 50,
                direction: 'horizontal',
                draggable: '.slick-dropped-grouping',
                dragoverBubble: false,
                revertClone: true,
                scroll: true,
                onEnd: (e: MouseEvent & { item: any; originalEvent: MouseEvent; }) => {
                    var sortArray = sortableInstance.toArray();
                    var newGroupingOrder = [];
                    for (var i = 0, l = sortArray.length; i < l; i++) {
                        for (var a = 0, b = this.columnsGroupBy.length; a < b; a++) {
                            if (this.columnsGroupBy[a].id == sortArray[i]) {
                                newGroupingOrder.push(this.columnsGroupBy[a]);
                                break;
                            }
                        }
                    }
                    this.columnsGroupBy = newGroupingOrder;
                    this.groupChanged(true);
                }
            });
        }

        this.expandAll.on('click', (e) => {
            (this.grid.getData() as any).expandAllGroups();
        });

        this.collapseAll.on('click', (e) => {
            (this.grid.getData() as any).collapseAllGroups();
        });
    }

    public getGroupingFor(column: Column): GroupInfo<any> {
        var g = null;

        if (this.options.getGroupingFor) {
            g = this.options.getGroupingFor(column);
        }

        if (g === false)
            return g;

        var fld = column.field || column.id;
        var asc = 1;
        var s = this.grid.getSortColumns();
        if (s != null && s.length > 0) {
            for (var x of s) {
                if (x.columnId == column.id) {
                    asc = x.sortAsc ? 1 : -1;
                    break;
                }
            }
        }

        return Object.assign({
            getter: fld,
            displayTotalsRow: this.options.displayTotalsRow,
            comparer: function (a, b) {
                return (a.value === b.value ? 0 :
                    (((a.value != null && b.value == null) || (a.value > b.value)) ? asc : -asc));
            },
            formatter: (g: Group<any>) => {
                var text;
                if (column.format || (column as any).formatter) {
                    var format = this.grid.getFormatter(-1, column);
                    if (format) {
                        var item = new NonDataRow();
                        var ctx = this.grid.getFormatterContext(-1, -1);
                        item["__grp"] = g;
                        item[column.field] = g.value;
                        ctx.item = item;
                        ctx.value = g.value;
                        try {
                            text = format(ctx);
                        }
                        catch (e) {
                            text = htmlEncode(g.value);
                        }
                    }
                    else
                        text = htmlEncode(g.value);
                }
                else
                    text = htmlEncode(g.value);

                var count = this.options.getCount ? this.options.getCount(g) : g.count;
                return '<span class="s-group-name">' +
                    (column.name || column.field || column.id) + ': </span>' +
                    text + ' <span class="s-group-count">(' +
                    (count || 0).toString() + ")</span>";
            },
            collapsed: true
        }, g);
    }

    private addGroupColumn(columnid: string): boolean {
        var columnAllowed = true;
        this.columnsGroupBy.forEach(function (clmn, i, a) {
            if (clmn.id == columnid) {
                columnAllowed = false;
            }
        });

        var gridColumns = this.getColumns();

        if (!columnAllowed)
            return;

        var columnAdded = false;
        gridColumns.forEach((col, i, a) => {
            if (col.id !== columnid || col.grouping === false)
                return;

            if (col.grouping == null || !Object.keys(col.grouping).length) {
                col.grouping = this.getGroupingFor(col);
                if (col.grouping as any === false)
                    return;
            }

            this.dropbox.append(
                <div class="slick-dropped-grouping" id={this.gridUid + col.id + "_entry"} data-id={col.id}>
                    <div style="display: inline-flex">{col.name}</div>
                    <div class={["slick-groupby-remove", this.options.deleteIconCssClass]} onClick={e =>
                        this.removeIconClick(col.id, Fluent(e.target).closest(".slick-dropped-grouping"))}></div>
                    <div></div>
                </div>);

            this.columnsGroupBy.push(col);
            columnAdded = true;
        });

        if (columnAdded) {
            this.grid.invalidate();
            return true;
        }
    }

    public setDroppedGroups(idList: string[]) {
        this.columnsGroupBy = [];
        this.dropbox.findAll(".slick-dropped-grouping").forEach(Fluent.remove);
        if (!idList || !idList.length)
            return;
        for (var i = 0; i < idList.length; i++) {
            this.addGroupColumn(idList[i]);
        }
        this.groupChanged(false);
    }

    private removeIconClick(id: string, entry: Fluent) {
        entry.remove();
        var changed = false;
        var idx;
        while ((idx = indexOf(this.columnsGroupBy, x => x.id == id)) >= 0) {
            this.columnsGroupBy.splice(idx, 1);
            changed = true;
        }
        changed && this.groupChanged(true);
    }

    private updateInterface() {
        var hasGrouping = !!(this.columnsGroupBy && this.columnsGroupBy.length);
        this.collapseAll.toggle(hasGrouping);
        this.expandAll.toggle(hasGrouping);
        Fluent.toggle(this.dropboxPlaceholder, !hasGrouping);
    }

    private groupChanged(userChange: boolean) {
        this.updateInterface();

        if (this.columnsGroupBy.length == 0) {
            (this.grid.getData() as any).setGrouping([]);
            this.onGroupChanged.notify({ groupColumns: [], userChange });
            this.grid.invalidate();
            return;
        }

        var groupingArray: any[] = [];
        this.columnsGroupBy.forEach(function (element) {
            groupingArray.push(element.grouping);
        });

        (this.grid.getData() as any).setGrouping(groupingArray);
        this.grid.invalidate();
        this.onGroupChanged.notify({ groupColumns: groupingArray, userChange });
    }
}

typeof globalThis != "undefined" && globalThis["Slick"] && globalThis["Slick"]["Plugins"] && (globalThis.Slick["Plugins"]["DraggableGrouping"] = DraggableGroupingPlugin);